import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DISPATCH_COLUMN } from '../../../../constants';

/**
 * useDispatchSelection - Hook for managing dispatch selection modes and logic
 *
 * This hook centralizes all selection-related state and operations for the dispatch module.
 * It supports three distinct selection modes (bill mode, print mode, create bill mode) and
 * handles both individual row selection and "select all" functionality across paginated data.
 *
 * ## Architecture Overview
 *
 * The hook manages two primary concerns:
 * 1. **Selection Modes**: Determines which UI mode is active and what actions are available
 * 2. **Selection Tracking**: Tracks which rows are selected using two strategies:
 *    - "page" mode: Tracks individual selected IDs in an array (poList)
 *    - "all" mode: Assumes all matching records selected, tracks exclusions instead
 *
 * ## Selection Mode Strategy
 *
 * When a user clicks "Select All Matching", switching to track every single selected ID
 * would be inefficient for large datasets (potentially thousands of records). Instead,
 * we flip to "all" mode and track only the IDs the user explicitly deselects (excludedIds).
 *
 * Example:
 * - Dataset has 5000 matching records
 * - User clicks "Select All Matching" → selectionMode='all', excludedIds={}
 * - User unchecks 3 rows → excludedIds={101, 205, 847}
 * - Selected count = 5000 - 3 = 4997
 * - When submitting, we send the exclusion list to the API instead of 4997 IDs
 *
 * ## Vendor-Specific Behavior
 *
 * Different vendor types have different rules for which rows can be selected in each mode.
 * This is controlled via the `selectionFilters` parameter:
 *
 * - Clothing vendors: Can select draft dispatches for grouping (billMode)
 * - Fabric/Dyeing vendors: Can select dispatched rows without bills for billing (createBillMode)
 * - All vendors: Can select for printing (printMode) based on vendor-specific rules
 *
 * @param {Object} options - Configuration options
 * @param {Array} options.dispatchData - Current page of dispatch records from the API
 * @param {Object} options.paginationData - Pagination metadata including total_records
 * @param {number} options.selectableRecords - Total count of records matching billMode filter across all pages
 * @param {number} options.printPackingSelectableRecords - Total count of records matching printMode filter
 * @param {number} options.createBillSelectableRecords - Total count of records matching createBillMode filter
 * @param {Object} options.columnConfig - Vendor-specific column visibility rules
 * @param {Array} options.columnConfig.show - Column names to explicitly show
 * @param {Array} options.columnConfig.hide - Column names to hide for this vendor
 * @param {Object} options.selectionFilters - Functions to determine row selectability per mode
 * @param {Function} options.selectionFilters.printMode - (row) => boolean for print selection
 * @param {Function} options.selectionFilters.billMode - (row) => boolean for bill/group selection
 * @param {Function} options.selectionFilters.createBillMode - (row) => boolean for create bill selection
 *
 * @returns {Object} Selection state, handlers, and computed values
 *
 * @example
 * // Basic usage in a dispatch component
 * const {
 *   billMode,
 *   handleAddBill,
 *   poList,
 *   addPOToBill,
 *   checked,
 *   selectedCount,
 * } = useDispatchSelection({
 *   dispatchData,
 *   paginationData,
 *   selectableRecords,
 *   selectionFilters: {
 *     billMode: (row) => row.dispatch_status === 'draft',
 *     printMode: (row) => row.dispatch_status === 'dispatched',
 *     createBillMode: (row) => !row.dispatch_bill_id,
 *   },
 * });
 */
export const useDispatchSelection = ({
  dispatchData = [],
  paginationData = {},
  selectableRecords = 0,
  printPackingSelectableRecords = 0,
  createBillSelectableRecords = 0,
  columnConfig = {
    show: [],
    hide: [],
  },
  /**
   * Default selection filters - can be overridden by vendor-specific components
   *
   * printMode: All rows are selectable by default
   * billMode: Only draft dispatches from dyeing partners or with vendor_type
   * createBillMode: Only dispatched/received rows without an existing bill
   */
  selectionFilters = {
    printMode: () => true,
    billMode: (row) =>
      row.dispatch_status === 'draft' &&
      (row?.partner_id?.vendor_type === 'dyeing' || row?.vendor_id?.vendor_type),
    createBillMode: (row) =>
      (row.dispatch_status === 'dispatched' || row.dispatch_status === 'received') &&
      !row.dispatch_bill_id,
  },
} = {}) => {
  // =============================================================================
  // URL STATE SYNCHRONIZATION
  // =============================================================================

  /**
   * Read initial mode states from URL parameters
   * This allows deep-linking to specific modes (e.g., /dispatch?billMode=true)
   */
  const [searchParams] = useSearchParams();

  // =============================================================================
  // MODE STATES
  // =============================================================================

  /**
   * billMode - Group Dispatch mode
   * When active, users can select multiple draft dispatches to group into a single bill.
   */
  const [billMode, setBillMode] = useState(searchParams.get('billMode') === 'true');

  /**
   * printMode - Print Packing List mode
   * When active, users can select dispatches to generate packing list PDFs.
   * Typically available for dispatches that have been validated/dispatched.
   */
  const [printMode, setPrintMode] = useState(searchParams.get('printMode') === 'true');

  /**
   * createBillMode - Create Bill mode (Fabric/Dyeing vendors only)
   * When active, users can select dispatched items to create a new vendor bill.
   * Only rows without an existing dispatch_bill_id are selectable.
   *
   * Note: Initial state reads from 'printMode' URL param - this appears to be intentional
   * to preserve backwards compatibility with existing URL structures.
   */
  const [createBillMode, setCreateBillMode] = useState(
    searchParams.get('printMode') === 'true',
  );

  // =============================================================================
  // SELECTION STATES
  // =============================================================================

  /**
   * poList - Array of selected dispatch objects (used in "page" selection mode)
   *
   * Each item contains: { id, box_count, shipment_no, container, dispatch_status }
   * This data is needed when submitting the selection to various API endpoints.
   *
   * Note: "PO" naming is historical - originally for Purchase Orders, now used for dispatches
   */
  const [poList, setPoList] = useState([]);

  /**
   * selectionMode - Determines how selections are tracked
   *
   * "page": Individual selections stored in poList (default)
   *         Use when: User manually selects/deselects individual rows
   *
   * "all":  All matching records considered selected, exclusions tracked in excludedIds
   *         Use when: User clicks "Select All X Matching Records"
   *
   * This optimization prevents memory issues when selecting thousands of records.
   */
  const [selectionMode, setSelectionMode] = useState('page'); // "page" | "all"

  /**
   * excludedIds - Set of IDs explicitly deselected when in "all" mode
   *
   * When selectionMode is "all", we assume all matching records are selected.
   * This Set tracks only the IDs the user has explicitly unchecked.
   *
   * Example: User selects all 5000 records, then unchecks 3
   *          excludedIds = Set(3) {101, 205, 847}
   *          Effective selection = 5000 - 3 = 4997 records
   */
  const [excludedIds, setExcludedIds] = useState(new Set());

  /**
   * globalSelectAll - Flag indicating "Select All Matching" has been clicked
   *
   * Used by UI to show appropriate messaging and determine when to display
   * the "Select All" link vs showing the count of selected items.
   */
  const [globalSelectAll, setGlobalSelectAll] = useState(false);

  // =============================================================================
  // COLUMN FILTERING STATE
  // =============================================================================

  /**
   * filteredColumn - The DISPATCH_COLUMN array filtered based on current mode and vendor config
   *
   * When no mode is active: Hides the checkbox column ('*')
   * When a mode is active: Shows checkbox column for row selection
   * Always applies vendor-specific column hiding from columnConfig.hide
   */
  const [filteredColumn, setFilteredColumn] = useState(DISPATCH_COLUMN);

  // =============================================================================
  // COMPUTED VALUES
  // =============================================================================

  /**
   * isAnyModeActive - Convenience boolean for checking if user is in any selection mode
   *
   * Used to determine:
   * - Whether to show checkbox column in table
   * - Whether certain actions are available
   * - UI styling and button states
   */
  const isAnyModeActive = billMode || printMode || createBillMode;

  // =============================================================================
  // SELECTION HANDLERS
  // =============================================================================

  /**
   * addPOToBill - Toggle selection state for a single row
   *
   * Behavior depends on current selectionMode:
   *
   * In "all" mode:
   *   - If ID is in excludedIds: Remove from exclusions (re-select it)
   *   - If ID is not excluded: Add to exclusions (deselect it)
   *
   * In "page" mode:
   *   - If ID already in poList: Remove it (deselect)
   *   - If ID not in poList: Add it with metadata (select)
   *
   * @param {Object} params - Row data
   * @param {number} params.id - Dispatch record ID
   * @param {number} params.box_count - Number of boxes in dispatch
   * @param {string} params.shipment_no - Shipment reference number
   * @param {string} params.container - Container number
   * @param {string} params.dispatch_status - Current status (draft, dispatched, etc.)
   */
  const addPOToBill = useCallback(
    ({ id, box_count, shipment_no, container, dispatch_status }) => {
      if (selectionMode === 'all') {
        // In "all" mode, toggle the exclusion status
        const next = new Set(excludedIds);
        if (excludedIds.has(id)) {
          next.delete(id); // Was excluded, now re-include (re-check the checkbox)
        } else {
          next.add(id); // Was included, now exclude (uncheck the checkbox)
        }
        setExcludedIds(next);
      } else {
        // In "page" mode, toggle presence in poList array
        if (poList.some((po) => po.id === id)) {
          // Already selected, remove it
          setPoList((prev) => prev.filter((po) => po.id !== id));
        } else {
          // Not selected, add it with full metadata
          // Prepend to array so newly selected items appear first if displayed
          setPoList((prev) => [
            { id, box_count, shipment_no, container, dispatch_status },
            ...prev,
          ]);
        }
      }
    },
    [selectionMode, excludedIds, poList],
  );

  /**
   * checked - Determine if a row's checkbox should be checked
   *
   * Used by PaginatedTable to render checkbox state for each row.
   *
   * @param {number} id - The dispatch record ID to check
   * @param {boolean} enable_check - Whether the row is selectable (based on filters)
   * @returns {boolean} True if checkbox should be checked
   *
   * Logic:
   * - In "all" mode: Checked if NOT in exclusions AND row is selectable
   * - In "page" mode: Checked if ID exists in poList
   */
  const checked = useCallback(
    (id, enable_check = true) => {
      if (selectionMode === 'all') {
        // In "all" mode, checked unless explicitly excluded
        // Also respect enable_check flag for rows that don't match current filter
        return !excludedIds.has(id) && enable_check;
      }
      // In "page" mode, check if ID is in our selected list
      return poList.some((po) => po.id === id);
    },
    [selectionMode, excludedIds, poList],
  );

  /**
   * handleSelectAllMatching - Select ALL records matching current filters across ALL pages
   *
   * This is the "Select All X Matching Records" action that appears after user
   * selects all visible items on a page.
   *
   * Implementation:
   * 1. Switch to "all" selection mode (track exclusions instead of inclusions)
   * 2. Clear any previous exclusions
   * 3. Clear poList (no longer needed in "all" mode)
   * 4. Set globalSelectAll flag for UI feedback
   *
   * After this, the selectedCount will equal the total matching records count
   * (selectableRecords, printPackingSelectableRecords, or createBillSelectableRecords
   * depending on active mode).
   */
  const handleSelectAllMatching = useCallback(() => {
    setSelectionMode('all');
    setExcludedIds(new Set());
    setPoList([]);
    setGlobalSelectAll(true);
  }, []);

  /**
   * handleSelectAllVisible - Select all selectable rows on the CURRENT PAGE only
   *
   * This is typically triggered by the table header checkbox.
   * Filters rows based on the active mode's selection criteria before selecting.
   *
   * Flow:
   * 1. Determine which filter to apply based on active mode
   * 2. Get IDs of all rows on current page that pass the filter
   * 3. Add each ID to selection if not already selected
   * 4. Ensure we're in "page" mode (not "all" mode)
   *
   * Note: This does NOT switch to "all" mode - it just adds visible items to poList.
   * User must explicitly click "Select All X Matching" to switch to "all" mode.
   */
  const handleSelectAllVisible = useCallback(() => {
    let visibleAssignedIds = [];

    if (printMode) {
      // Print mode: Apply vendor-specific print selection filter
      visibleAssignedIds = dispatchData
        .filter(selectionFilters.printMode)
        .map((row) => row.id);
    } else if (createBillMode) {
      // Create bill mode: Only dispatched/received rows without existing bill
      visibleAssignedIds = dispatchData
        .filter(selectionFilters.createBillMode)
        .map((row) => row.id);
    } else {
      // Bill mode (default): Apply vendor-specific bill selection filter
      visibleAssignedIds = dispatchData
        .filter(selectionFilters.billMode)
        .map((row) => row.id);
    }

    // Add each ID if not already checked
    // Using addPOToBill with minimal data since we only need the ID for bulk selection
    visibleAssignedIds.forEach((id) => {
      if (!checked(id)) {
        addPOToBill({ id });
      }
    });

    // Ensure we're in page mode (select all visible doesn't imply select all matching)
    setSelectionMode('page');
    setExcludedIds(new Set());
  }, [
    printMode,
    createBillMode,
    dispatchData,
    selectionFilters,
    checked,
    addPOToBill,
  ]);

  // =============================================================================
  // MODE TOGGLE HANDLERS
  // =============================================================================

  /**
   * handleAddBill - Toggle bill mode (Group Dispatch mode) on/off
   *
   * When turning OFF: Clears all selection state to start fresh
   * When turning ON: Just sets the mode flag, selection state remains empty
   *
   * The "Group Dispatch" button in the UI calls this handler.
   */
  const handleAddBill = useCallback(() => {
    if (billMode) {
      // Turning off - reset all selection state
      setBillMode(false);
      setPoList([]);
      setSelectionMode('page');
      setExcludedIds(new Set());
      setGlobalSelectAll(false);
    } else {
      // Turning on - just activate the mode
      setBillMode(true);
    }
  }, [billMode]);

  /**
   * handleAddPrint - Toggle print mode (Print Packing List mode) on/off
   *
   * Same pattern as handleAddBill - clears selection when turning off.
   * The "Print Packing List" button in the UI calls this handler.
   */
  const handleAddPrint = useCallback(() => {
    if (printMode) {
      // Turning off - reset all selection state
      setPrintMode(false);
      setPoList([]);
      setSelectionMode('page');
      setExcludedIds(new Set());
      setGlobalSelectAll(false);
    } else {
      // Turning on - just activate the mode
      setPrintMode(true);
    }
  }, [printMode]);

  /**
   * handleCreateBillMode - Toggle create bill mode on/off
   *
   * Same pattern as other mode handlers.
   * The "Create Bill" button (Fabric/Dyeing vendors) calls this handler.
   */
  const handleCreateBillMode = useCallback(() => {
    if (createBillMode) {
      // Turning off - reset all selection state
      setCreateBillMode(false);
      setPoList([]);
      setSelectionMode('page');
      setExcludedIds(new Set());
      setGlobalSelectAll(false);
    } else {
      // Turning on - just activate the mode
      setCreateBillMode(true);
    }
  }, [createBillMode]);

  // =============================================================================
  // UTILITY HANDLERS
  // =============================================================================

  /**
   * clearSelection - Reset all selection state without changing modes
   *
   * Called after successful API actions (submit dispatch, print, create bill)
   * to clear the selection and allow user to make new selections.
   *
   * Does NOT turn off the active mode - user stays in their current mode.
   */
  const clearSelection = useCallback(() => {
    setPoList([]);
    setSelectionMode('page');
    setExcludedIds(new Set());
    setGlobalSelectAll(false);
  }, []);

  /**
   * resetSelectionForModeChange - Partial reset when switching between modes
   *
   * Lighter reset than clearSelection - only resets the "all" mode state
   * but preserves poList. Used when user switches from one mode to another
   * without explicitly canceling.
   */
  const resetSelectionForModeChange = useCallback(() => {
    setGlobalSelectAll(false);
    setSelectionMode('page');
  }, []);

  // =============================================================================
  // MODE GETTER FUNCTIONS
  // =============================================================================

  /**
   * Mode getter functions for PaginatedTable component
   *
   * PaginatedTable needs to know which mode is active to:
   * - Enable/disable row checkboxes based on selection filters
   * - Apply appropriate styling
   * - Determine checkbox column visibility
   *
   * These are simple getter functions (not useCallback) because:
   * 1. They're trivial operations
   * 2. PaginatedTable is not memoized, so stable references don't help
   * 3. Avoids unnecessary complexity
   */
  const isBillMode = () => billMode;
  const isPrintMode = () => printMode;
  const isCreateBillMode = () => createBillMode;

  // =============================================================================
  // COMPUTED VALUES (MEMOIZED)
  // =============================================================================

  /**
   * selectedCount - Total number of currently selected records
   *
   * Calculation depends on selectionMode:
   *
   * "all" mode: Total matching records - number of exclusions
   *   - Uses the appropriate count based on active mode
   *   - printPackingSelectableRecords for print mode
   *   - createBillSelectableRecords for create bill mode
   *   - selectableRecords for bill mode
   *
   * "page" mode: Simply the length of poList
   *
   * Used in UI to show "X records selected" message.
   */
  const selectedCount = useMemo(() => {
    if (selectionMode === 'all') {
      // In "all" mode, calculate as total - exclusions
      if (printMode) {
        return printPackingSelectableRecords - excludedIds.size;
      }
      if (createBillMode) {
        return createBillSelectableRecords - excludedIds.size;
      }
      if (billMode) {
        return selectableRecords - excludedIds.size;
      }
      return 0;
    }
    // In "page" mode, just count the poList
    return poList.length;
  }, [
    selectionMode,
    printMode,
    createBillMode,
    billMode,
    poList.length,
    excludedIds.size,
    printPackingSelectableRecords,
    createBillSelectableRecords,
    selectableRecords,
  ]);

  /**
   * totalSelectableForMode - Maximum number of records that CAN be selected in current mode
   *
   * Each mode has different criteria for what's selectable:
   * - Print mode: Records matching printMode filter
   * - Create bill mode: Records matching createBillMode filter
   * - Bill mode: Records matching billMode filter
   * - No mode active: Total records in dataset
   *
   * Used to show "Select all X matching records" link and to calculate
   * whether all selectable records are currently selected.
   */
  const totalSelectableForMode = useMemo(() => {
    if (printMode) {
      return printPackingSelectableRecords;
    }
    if (createBillMode) {
      return createBillSelectableRecords;
    }
    if (billMode) {
      return selectableRecords;
    }
    return paginationData?.total_records || 0;
  }, [
    printMode,
    createBillMode,
    billMode,
    printPackingSelectableRecords,
    createBillSelectableRecords,
    selectableRecords,
    paginationData,
  ]);

  // =============================================================================
  // EFFECTS
  // =============================================================================

  /**
   * Effect: Update filtered columns when mode or vendor config changes
   *
   * Column filtering logic:
   * 1. When ANY mode is active: Show full DISPATCH_COLUMN (including checkbox column '*')
   * 2. When NO mode is active: Hide the checkbox column ('*')
   * 3. Always apply vendor-specific hiding from columnConfig.hide
   *
   * The filtered columns are passed to PaginatedTable for rendering.
   */
  useEffect(() => {
    // Start with full column set or without checkbox based on mode
    let columns = isAnyModeActive
      ? DISPATCH_COLUMN
      : DISPATCH_COLUMN.filter((col) => col.name !== '*');

    // Apply vendor-specific column hiding
    columns = columns.filter((col) => {
      // Hide columns specified in vendor's columnConfig
      if (columnConfig.hide.includes(col.name)) {
        return false;
      }
      return true;
    });

    setFilteredColumn(columns);
  }, [billMode, printMode, createBillMode, isAnyModeActive, columnConfig.hide]);

  // =============================================================================
  // RETURN VALUE
  // =============================================================================

  return {
    // -------------------------------------------------------------------------
    // Mode States - Current state of each selection mode
    // -------------------------------------------------------------------------
    billMode,           // Boolean - Group Dispatch mode active
    setBillMode,        // Direct setter (rarely needed, prefer handleAddBill)
    printMode,          // Boolean - Print Packing List mode active
    setPrintMode,       // Direct setter (rarely needed, prefer handleAddPrint)
    createBillMode,     // Boolean - Create Bill mode active
    setCreateBillMode,  // Direct setter (rarely needed, prefer handleCreateBillMode)
    isAnyModeActive,    // Boolean - true if any mode is active

    // -------------------------------------------------------------------------
    // Selection States - Track what's selected
    // -------------------------------------------------------------------------
    poList,             // Array of selected dispatch objects (page mode)
    setPoList,          // Direct setter for poList
    selectionMode,      // "page" or "all"
    setSelectionMode,   // Direct setter for selection mode
    excludedIds,        // Set of excluded IDs (all mode)
    setExcludedIds,     // Direct setter for exclusions
    globalSelectAll,    // Boolean - "Select All Matching" was clicked
    setGlobalSelectAll, // Direct setter for globalSelectAll flag

    // -------------------------------------------------------------------------
    // Column Configuration
    // -------------------------------------------------------------------------
    filteredColumn,     // DISPATCH_COLUMN filtered by mode and vendor config

    // -------------------------------------------------------------------------
    // Computed Values - Derived from selection state
    // -------------------------------------------------------------------------
    selectedCount,          // Number of currently selected records
    totalSelectableForMode, // Max selectable records for current mode

    // -------------------------------------------------------------------------
    // Handlers - Functions to modify selection state
    // -------------------------------------------------------------------------
    addPOToBill,              // Toggle selection for a single row
    checked,                  // Check if a row is selected
    handleSelectAllMatching,  // Select all records across all pages
    handleSelectAllVisible,   // Select all visible records on current page
    handleAddBill,            // Toggle bill mode
    handleAddPrint,           // Toggle print mode
    handleCreateBillMode,     // Toggle create bill mode
    clearSelection,           // Clear selection without changing mode
    resetSelectionForModeChange, // Partial reset for mode switching

    // -------------------------------------------------------------------------
    // Mode Getter Functions - For PaginatedTable compatibility
    // -------------------------------------------------------------------------
    isBillMode,       // () => billMode
    isPrintMode,      // () => printMode
    isCreateBillMode, // () => createBillMode
  };
};
